/**
 * Matching engine for the Schedule module: given a pickup date, which
 * rules and which strategies apply, and how many runs that adds up to.
 *
 * Pure arithmetic, no I/O. Rules and strategies used to be read from
 * `localStorage` here (`ds_schedule_rules_v1`); since CU-869f043cf they
 * come from webback through `@services/useSchedule`, and this file kept
 * only the part that does not care where they came from.
 *
 * The filename is now a lie and is kept for one release so the diff of
 * that mission stays readable; it is `scheduleMatching.js` in spirit.
 *
 * All dates are ISO strings ('YYYY-MM-DD'). Times are 'HH:MM' 24h.
 */


// ---------------------------------------------------------------------------
// Schedule rules
// ---------------------------------------------------------------------------
export const FREQUENCY_KIND = {
	EVERY_N_HOURS:   'every_n_hours',    // { value: 3 }  → every 3 hours in active window
	SPECIFIC_HOURS:  'specific_hours',   // { value: ['09:00','18:00'] } → literal list
}

// Schedule rules do NOT filter by acriss — the frequency of engine
// runs is decided by office (a fleet's operational unit), not by
// vehicle category. Strategies do filter by acriss for pricing logic.
//
// Overlaps ARE allowed: two rules can cover the same pickup date +
// office. `priority` resolves them (lower number wins). Use case: a
// custom high-priority rule for a specific event week overrides the
// general rule without editing it.
export const DEFAULT_RULES = [
	{
		id: 'r1', name: 'Last week', is_active: true, priority: 10,
		mode: 'rolling', dba_from: 0, dba_to: 7,
		fixed_from: null, fixed_to: null,
		frequency_kind: 'every_n_hours', frequency_value: 1,
		window_start: '06:00', window_end: '22:00',
		weekdays: ['mon','tue','wed','thu','fri','sat','sun'],
		office_codes: [],
		pickup_hour: null, dropoff_hour: null,   // null = cualquier hora
	},
	{
		id: 'r2', name: 'Close-in', is_active: true, priority: 20,
		mode: 'rolling', dba_from: 8, dba_to: 30,
		fixed_from: null, fixed_to: null,
		frequency_kind: 'every_n_hours', frequency_value: 3,
		window_start: '06:00', window_end: '22:00',
		weekdays: ['mon','tue','wed','thu','fri','sat','sun'],
		office_codes: [],
		pickup_hour: null, dropoff_hour: null,
	},
	{
		id: 'r3', name: 'Mid-term', is_active: true, priority: 30,
		mode: 'rolling', dba_from: 31, dba_to: 90,
		fixed_from: null, fixed_to: null,
		frequency_kind: 'specific_hours', frequency_value: ['09:00','18:00'],
		window_start: '00:00', window_end: '23:59',
		weekdays: ['mon','tue','wed','thu','fri'],
		office_codes: [],
		pickup_hour: '10:00', dropoff_hour: '10:00',
	},
	{
		id: 'r4', name: 'Long-term', is_active: false, priority: 40,
		mode: 'rolling', dba_from: 91, dba_to: 365,
		fixed_from: null, fixed_to: null,
		frequency_kind: 'specific_hours', frequency_value: ['09:00'],
		window_start: '00:00', window_end: '23:59',
		weekdays: ['mon','tue','wed','thu','fri','sat','sun'],
		office_codes: [],
		pickup_hour: null, dropoff_hour: null,
	},
]

// Rules and strategies used to live in localStorage here. They are real
// data now: rules come from `/api/repricing/schedule/rules` and strategies
// from `/api/repricing/strategies`, through `@services/useSchedule`. What
// stays in this file is the pure matching engine below, which is the same
// arithmetic whatever the source — and `DEFAULT_RULES`, kept only as the
// shape reference the drawer builds a new rule from.
//
// Nothing seeds a tenant any more: an operator with no rules sees an empty
// state and creates the first one. Four invented rules that look like real
// configuration are worse than a blank screen (brief CU-869f043cf, criterio 17).

// ---------------------------------------------------------------------------
// Matching engine (pure functions — no DB)
// ---------------------------------------------------------------------------
const WEEKDAY_ORDER = ['sun','mon','tue','wed','thu','fri','sat']
export const WEEKDAY_LABELS = { mon:'Mo', tue:'Tu', wed:'We', thu:'Th', fri:'Fr', sat:'Sa', sun:'Su' }

export function weekdayCode(date) {
	return WEEKDAY_ORDER[date.getDay()]
}

export function daysBetween(a, b) {
	const ms = new Date(b).setHours(0,0,0,0) - new Date(a).setHours(0,0,0,0)
	return Math.round(ms / (1000 * 60 * 60 * 24))
}

function isoDate(d) {
	const yr = d.getFullYear(), mo = String(d.getMonth()+1).padStart(2,'0'),
	      dy = String(d.getDate()).padStart(2,'0')
	return `${yr}-${mo}-${dy}`
}

/**
 * Is this pickup date inside a `MM-DD` season?
 *
 * A season recurs — "high season is July" is about every July — so the
 * canonical strategy stores it without a year and the engine compares
 * month-day against month-day (`sweep/period.py`). This used to compare
 * the full `YYYY-MM-DD` against `MM-DD`, which is never true, so no
 * strategy with a season ever applied on the calendar and every cell
 * said "no active strategy" (Curro, 2026-10-02). A season that wraps
 * the new year (`12-15` → `01-10`) is the union of its two halves.
 */
export function inSeason(date, from, to) {
	const md = isoDate(date).slice(5)
	return from <= to
		? md >= from && md <= to
		: md >= from || md <= to
}

function scopeMatchesOfficeOnly(entity, officeFilter) {
	const eOffices = entity.office_codes || []
	if (eOffices.length && officeFilter.length &&
		!eOffices.some(x => officeFilter.includes(x))) return false
	return true
}

function scopeMatchesOfficeAndSipp(entity, officeFilter, sippFilter) {
	const eOffices = entity.office_codes || []
	const eSipps   = entity.sipp_codes   || []
	if (eOffices.length && officeFilter.length &&
		!eOffices.some(x => officeFilter.includes(x))) return false
	if (eSipps.length && sippFilter.length &&
		!eSipps.some(x => sippFilter.includes(x))) return false
	return true
}

function ruleCoversDay(rule, pickupDate, today, officeFilter) {
	if (!rule.is_active) return { covers: false, reason: 'paused' }
	if (!rule.weekdays.includes(weekdayCode(pickupDate)))
		return { covers: false, reason: 'weekday not in filter' }
	if (rule.mode === 'rolling') {
		const dba = daysBetween(today, pickupDate)
		if (dba < rule.dba_from || dba > rule.dba_to)
			return { covers: false, reason: `out of DBA range (${rule.dba_from}-${rule.dba_to})` }
	} else {
		const iso = isoDate(pickupDate)
		if (iso < rule.fixed_from || iso > rule.fixed_to)
			return { covers: false, reason: `out of fixed range (${rule.fixed_from}..${rule.fixed_to})` }
	}
	if (!scopeMatchesOfficeOnly(rule, officeFilter))
		return { covers: false, reason: 'office scope mismatch' }
	return { covers: true }
}

function stratCoversDay(strat, pickupDate, today, officeFilter, sippFilter) {
	if (!strat.is_active) return { covers: false, reason: 'paused' }
	if (!strat.weekdays.includes(weekdayCode(pickupDate)))
		return { covers: false, reason: 'weekday not in filter' }
	const dba = daysBetween(today, pickupDate)
	if (dba < strat.dba_from || dba > strat.dba_to)
		return { covers: false, reason: `out of lead time (DBA ${strat.dba_from}-${strat.dba_to})` }
	if (strat.season_from && strat.season_to && !inSeason(pickupDate, strat.season_from, strat.season_to))
		return { covers: false, reason: `out of season (${strat.season_from}..${strat.season_to})` }
	if (!scopeMatchesOfficeAndSipp(strat, officeFilter, sippFilter))
		return { covers: false, reason: 'office/acriss scope mismatch' }
	return { covers: true }
}

/**
 * Expand a rule's frequency spec into the list of hours it runs on a
 * given day (already known to be covered by the rule).
 */
export function ruleHoursForDay(rule) {
	const [wsH] = rule.window_start.split(':').map(Number)
	const [weH] = rule.window_end.split(':').map(Number)
	if (rule.frequency_kind === 'specific_hours') {
		return [...(rule.frequency_value || [])].sort()
	}
	if (rule.frequency_kind === 'every_n_hours') {
		const step = Math.max(1, rule.frequency_value)
		const out = []
		for (let h = wsH; h <= weH; h += step) out.push(`${String(h).padStart(2,'0')}:00`)
		return out
	}
	return []
}

/**
 * For a pickup date, returns { applicableRules[], notApplicableRules[],
 *                              applicableStrategies[], notApplicableStrategies[] }
 * Ordered by priority (strategies) and by DBA range width (rules — narrower
 * covers first, informational only, since we allow scope-based overlaps).
 */
export function evaluateDay({ pickupDate, today, rules, strategies,
                              officeFilter, sippFilter }) {
	const applicableRules = []
	const notApplicableRules = []
	for (const rule of rules) {
		const r = ruleCoversDay(rule, pickupDate, today, officeFilter)
		if (r.covers) applicableRules.push(rule)
		else          notApplicableRules.push({ ...rule, reason: r.reason })
	}
	applicableRules.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))

	const applicableStrategies = []
	const notApplicableStrategies = []
	for (const strat of strategies) {
		const r = stratCoversDay(strat, pickupDate, today, officeFilter, sippFilter)
		if (r.covers) applicableStrategies.push(strat)
		else          notApplicableStrategies.push({ ...strat, reason: r.reason })
	}
	applicableStrategies.sort((a, b) => a.priority - b.priority)
	notApplicableStrategies.sort((a, b) => a.priority - b.priority)

	return {
		applicableRules, notApplicableRules,
		applicableStrategies, notApplicableStrategies,
	}
}

/**
 * Reject overlap: two rules with intersecting office scope AND
 * overlapping DBA range are not allowed. Disjoint office scopes
 * are allowed on the same DBA range so a fleet can be sliced by
 * airport / city.
 */
export function findOverlap(candidate, rules) {
	for (const r of rules) {
		if (r.id === candidate.id) continue
		if (!r.is_active) continue
		const dbaOverlap = candidate.dba_from <= r.dba_to && candidate.dba_to >= r.dba_from
		if (!dbaOverlap) continue
		if (arraysIntersectOrAll(candidate.office_codes, r.office_codes)) return r
	}
	return null
}

function arraysIntersectOrAll(a, b) {
	if (!a?.length || !b?.length) return true   // "all" on either side
	return a.some(x => b.includes(x))
}
